// useMapEditor.js - bridges OL interactions to the ProseMirror document.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { undo, redo, undoDepth, redoDepth } from 'prosemirror-history';
import { extractMapState, layersForPanel, markingsToGeoJSON, resolveTrackAtDtg, getTrailSnapshots, DEFAULT_MARKING_COLOR } from '../schema/mapPmAdapter';
import { mapSchema, newLayerId, newViewId, newTrackId, newMarkingSnapshot } from '../schema/mapSchema';
import { renderTacticalGraphic } from './renderer/TacticalRenderer';
import { clearCache as clearSymbolCacheExt, getMobilityForSidc, getAffiliationForSidc } from './renderer/SymbolRenderer';
import {
    symbolStyleForSidc, clearSymbolStyleCache, markingFeatureStyle, tacticalFeatureStyle,
    expandTacticalMarkingsToOlFeatures, hexWithOpacity,
} from './renderer/markingStyle';
import { AreaFillOverlay } from './renderer/AreaFillOverlay';
import { applyTrailOpacity } from './renderer/staleRendering';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import Draw from 'ol/interaction/Draw';
import Modify from 'ol/interaction/Modify';
import Select from 'ol/interaction/Select';
import Snap from 'ol/interaction/Snap';
import { click } from 'ol/events/condition';
import { Style, Fill, Stroke, Circle as CircleStyle } from 'ol/style';
import { Circle as OLCircleGeom } from 'ol/geom';
import { fromLonLat, toLonLat } from 'ol/proj';
import { getDistance } from 'ol/sphere';
import { Modifiers } from '@armyc2.c5isr.renderer/mil-sym-ts-web';
import { getDrawInteractionFactory } from './customDrawInteractions';
import { generateSpread } from './customInteractions/geometryUtils';
import { ensurePreviewLayer, clearPreviewLayer } from './customInteractions/livePreview';
import { unByKey } from 'ol/Observable';

const DEBOUNCE_MS = 100;

// ticket 04: throttle window for the DTG-scrub-driven tactical/trail resync below — mirrors the
// ~150-200ms setTimeout/clearTimeout trailing-edge pattern already used by
// `useSatelliteImagery`'s moveend swap and `useCoordGridOverlay`'s grid redraw.
const DTG_SCRUB_THROTTLE_MS = 150;

// ticket 01/03: shared identity/mobility/snapshot attrs stamped onto every newly-created marking
// node, factored out of the ~6 map_marking.create({...}) call sites below so the append-only
// `snapshots` seed and `trackId`/`mobility` derivation logic lives in exactly one place.
//
// `author` is intentionally NOT renamed to `createdBy` — see the comment on the `author` attr
// in mapSchema.js for why (backward-compat with already-persisted Yjs docs). Semantically
// `author` IS createdBy; this helper's `uid` param feeds that same field.
//
// ticket 01: there is no top-level `sidc`/`sidcModifiers`/`label`/`geometry` attr anymore — this
// helper writes ALL of a marking's initial renderable data into the single seed snapshot's
// `props` (plus `geometry` alongside it), so every creation call site only needs to spread this
// helper's return value (and any non-time-varying attrs like `graphicType`) into `.create()`.
function creationIdentityAttrs({ uid, now, sidc, sidcModifiers, label, geometry, dtg }) {
    return {
        author: uid,
        createdAt: now,
        updatedAt: now,
        trackId: newTrackId(),
        mobility: getMobilityForSidc(sidc),
        snapshots: [newMarkingSnapshot(dtg ?? now, geometry, {
            sidc: sidc ?? null,
            sidcModifiers: sidcModifiers ?? null,
            label: label ?? '',
            // Affiliation is only meaningful for SIDC-backed symbols — leave it null for plain
            // free-draw shapes rather than letting getAffiliationForSidc's error-path default
            // ('friend') mislabel them.
            affiliation: sidc ? getAffiliationForSidc(sidc) : null,
        })],
    };
}

// ticket 02/03: resolve-then-update-or-fork snapshot helper for the two edit paths below
// (modifyend vertex-drag, updateMarkingProps from the right panel). An edit always targets
// "the snapshot at the DTG cursor is currently scrubbed to" (`dtg` here), resolved via
// resolveTrackAtDtg's "nearest snapshot at-or-before `dtg`" pick against `existingSnapshots`:
//
//   - Exact match (`picked.dtg === dtg`) → merge the patch into THAT entry in place. Every
//     other entry in the returned array is the same reference as before — never touched.
//   - No exact match → fork a brand-new entry at `dtg`, seeded by cloning the picked "nearest
//     earlier" snapshot's full geometry+props (any field the patch doesn't supply carries
//     forward from that clone instead of being blanked), then the patch is applied on top.
//     This is what lets an edit made after scrubbing to a historical DTG land a correctly
//     positioned/populated new history point instead of a corrupted duplicate.
//   - No earlier snapshot exists at all (editing before any recorded history) → there's
//     nothing to clone from; the new entry only has whatever the patch itself supplies —
//     the same "nothing to fall back to" situation resolveTrackAtDtg treats as unresolvable.
//
// Repeated amplifier/modifier edits while the DTG cursor hasn't moved keep landing on the same
// exact-match entry (no duplicate track point at the same position — this is also what
// previously caused the movement-trail ghost renderer, syncTrailSource, to draw overlapping
// "duplicate" symbols stacked on the same spot).
export function appendMarkingSnapshot(existingSnapshots, { dtg, geometry, props }) {
    const prev = Array.isArray(existingSnapshots) ? existingSnapshots : [];
    const resolvedDtg = dtg ?? Date.now();
    const base = resolveTrackAtDtg({ snapshots: prev }, resolvedDtg).snapshot;

    const nextGeometry = geometry !== undefined ? geometry : (base?.geometry ?? null);
    const nextProps = { ...(base?.props || {}), ...(props || {}) };
    const nextEntry = newMarkingSnapshot(resolvedDtg, nextGeometry, nextProps);

    if (base && base.dtg === resolvedDtg) {
        return prev.map((s) => (s.dtg === resolvedDtg ? nextEntry : s));
    }
    return [...prev, nextEntry];
}

// ticket 01/03: `selectedMarkings` (and any other place the UI panel reads a marking's data)
// needs its geometry/sidc/sidcModifiers/label/affiliation flattened back onto the object for the
// existing panel components (ContextPanel/SymbolEditor/TacticalMarkingEditor/ShapeForm), which
// still read those as plain top-level fields.
//
// ticket 03: `cursorDtg` (the live DTG cursor, `null`/`undefined` meaning "always latest", same
// contract as `resolveTrackAtDtg` itself) is threaded through here so the panel shows the
// snapshot that's actually live at the cursor position rather than always the newest one. The
// resolved `isCurrent` flag (see resolveTrackAtDtg's doc comment) is carried onto the flattened
// object so the panel can render the shared "LAST KNOWN" badge treatment when the cursor doesn't
// land on an exact snapshot — e.g. ContextPanel.jsx.
//
// ticket 04 (merge-last-known-into-trail): `trailEnabled`/`trailWindowDays` (the same existing
// "Show trail" state threaded through the map-render sync functions below) bound how stale a
// "last known" pick may be before it's suppressed rather than shown with the badge — mirrors
// `resolveTrackAtDtg`'s `maxAgeDays` contract exactly (`0` when the trail is off, `trailWindowDays`
// when it's on). The resolved `visible` flag is carried onto the flattened object too, so
// ContextPanel can tell "stale but within the trail window" (badge) apart from "suppressed — same
// as gone from the map" (no badge) even though `isCurrent` is `false` in both cases.
export function flattenMarkingForPanel(marking, cursorDtg, trailEnabled = false, trailWindowDays = 7) {
    const maxAgeDays = trailEnabled ? trailWindowDays : 0;
    const resolved = resolveTrackAtDtg(marking, cursorDtg, maxAgeDays);
    const props = resolved.snapshot?.props || {};
    return {
        ...marking,
        geometry: resolved.snapshot?.geometry ?? null,
        sidc: props.sidc ?? null,
        sidcModifiers: props.sidcModifiers ?? null,
        label: props.label ?? '',
        affiliation: props.affiliation ?? null,
        isCurrent: resolved.isCurrent,
        visible: resolved.visible,
    };
}

const FORMAT = new GeoJSON({ featureProjection: 'EPSG:3857', dataProjection: 'EPSG:4326' });

// Layer IDs stored as properties on OL layer objects
const LYR_FILLS = 'ol-fills';
const LYR_PREVIEW = 'ol-preview';
const LYR_TACTICAL = 'ol-tactical';
const LYR_TRAIL = 'ol-trail'; // ticket 05: movement trail ghosts + connecting line

// ticket 05: fixed semi-transparent opacity for the trail's connecting line (the per-snapshot
// fade — TRAIL_MIN_OPACITY..TRAIL_MAX_OPACITY from getTrailSnapshots — is only for the ghost
// symbols; the ticket calls for "a semi-transparent connecting line", a single simple stroke,
// not itself fading along its length).
const TRAIL_LINE_OPACITY = 0.35;

function debounce(fn, ms) {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
}

function findNodePos(doc, typeName, idAttr, id) {
    let found = null;
    doc.descendants((node, pos) => {
        if (node.type.name === typeName && node.attrs[idAttr] === id) { found = pos; return false; }
    });
    return found;
}

function ensureDefaultLayer(state) {
    const { layers } = extractMapState(state.doc);
    if (layers.length > 0) return { layerId: layers[0].layerId, tr: null };
    const layerId = newLayerId();
    const tr = state.tr.insert(state.doc.content.size,
        mapSchema.nodes.map_layer.create({ layerId, label: 'Layer 1', color: '#3b82f6', visible: true, opacity: 1, order: 0 }));
    return { layerId, tr };
}

function elementTypeFromGeom(geomType) {
    if (geomType === 'Point') return 'point';
    if (geomType === 'LineString') return 'free-line';
    return 'free-polygon';
}

// ---------------------------------------------------------------------------
// Symbol/marking/tactical styling — this hook (live editor) and MapPreviewEngine.js
// (report-embed preview) both call straight into the shared implementation in
// renderer/markingStyle.js, so there is exactly one "marking -> OL Style" pipeline with no
// per-pipeline wrapper layer to keep in sync.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// ticket 05: movement trail — ghost symbols (past snapshots, faded) + a connecting line to the
// current position, for `mobility === 'moving'` tracks when the "Show movement trail" toggle is
// on. Deliberately a thin *sibling* of markingFeatureStyle rather than a branch inside it: ghosts
// need a per-feature fade fraction (`getTrailSnapshots`' `opacity`, applied via
// `applyTrailOpacity` — no "LAST KNOWN" badge, see staleRendering.js) instead of the fixed
// STALE_OPACITY/badge treatment `markingFeatureStyle` applies for stale "last known" symbols, and
// the connecting-line feature kind has no equivalent in markingFeatureStyle at all.
// ---------------------------------------------------------------------------
function trailGhostStyle(symbolSettings, props) {
    if (props.sidc) {
        const mods = props.sidcModifiers
            ? (typeof props.sidcModifiers === 'string' ? JSON.parse(props.sidcModifiers) : props.sidcModifiers)
            : {};
        const style = symbolStyleForSidc(props.sidc, mods);
        if (style) return applyTrailOpacity(style, props.opacity);
    }
    const color = props.color || '#3b82f6';
    const style = new Style({ image: new CircleStyle({ radius: 5, fill: new Fill({ color }), stroke: new Stroke({ color: '#fff', width: 1.5 }) }) });
    return applyTrailOpacity(style, props.opacity);
}

function trailStyleFn(symbolSettings) {
    return (feature) => {
        const props = feature.get('_props') || {};
        if (props.kind === 'line') {
            return new Style({ stroke: new Stroke({ color: hexWithOpacity(props.color || '#3b82f6', TRAIL_LINE_OPACITY), width: 2, lineDash: [2, 5] }) });
        }
        return trailGhostStyle(symbolSettings, props);
    };
}

function previewStyleFn() {
    return [
        new Style({ fill: new Fill({ color: 'rgba(59,130,246,0.1)' }), stroke: new Stroke({ color: '#3b82f6', width: 2, lineDash: [4, 2] }) }),
        new Style({ image: new CircleStyle({ radius: 4, fill: new Fill({ color: '#3b82f6' }), stroke: new Stroke({ color: '#fff', width: 1.5 }) }) }),
    ];
}

// Circle sketch style: converts the 2-pt LineString sketch into a visual
// circle ring so the user sees the growing circle before the second click.
// ponytail: ol/geom/Circle is Euclidean (EPSG:3857); good enough for visual preview.
const CIRCLE_SKETCH_STYLES = [
    new Style({
        geometry: (feature) => {
            const coords = feature.getGeometry()?.getCoordinates?.();
            if (!coords || coords.length < 2) return null;
            const center = coords[0];
            const edge   = coords[coords.length - 1];
            const dx = edge[0] - center[0], dy = edge[1] - center[1];
            return new OLCircleGeom(center, Math.sqrt(dx * dx + dy * dy));
        },
        stroke: new Stroke({ color: '#3b82f6', width: 2, lineDash: [4, 2] }),
    }),
    // Center dot
    new Style({
        geometry: (feature) => {
            const coords = feature.getGeometry()?.getCoordinates?.();
            return coords?.length ? new OLCircleGeom(coords[0], 0) : null;
        },
        image: new CircleStyle({ radius: 4, fill: new Fill({ color: '#3b82f6' }), stroke: new Stroke({ color: '#fff', width: 1.5 }) }),
    }),
    // Faint radius line for distance hint
    new Style({
        stroke: new Stroke({ color: 'rgba(59,130,246,0.35)', width: 1, lineDash: [3, 4] }),
    }),
];

// ---------------------------------------------------------------------------
// OL source/layer setup
// ---------------------------------------------------------------------------
function setupOlLayers(map, symbolSettings, selectedIdsRef) {
    const existing = {};
    map.getLayers().getArray().forEach((l) => { const id = l.get('id'); if (id) existing[id] = l; });
    if (existing[LYR_FILLS]) return existing; // already set up

    const markingsSource = new VectorSource();
    const previewSource = new VectorSource();
    const tacticalSource = new VectorSource();
    const trailSource = new VectorSource();

    // ticket 05: trail ghosts + connecting line render *below* the live markings layer (zIndex 2)
    // — the current, full-opacity position should always read as "on top of" its own faded
    // history, never occluded by a ghost sharing the same screen position.
    const trailLayer = new VectorLayer({ source: trailSource, style: (f) => trailStyleFn(symbolSettings)(f), zIndex: 1.5 });
    trailLayer.set('id', LYR_TRAIL);

    const markingsLayer = new VectorLayer({
        source: markingsSource,
        style: (f) => markingFeatureStyle(f, selectedIdsRef.current),
        zIndex: 2,
    });
    markingsLayer.set('id', LYR_FILLS);

    const previewLayer = new VectorLayer({ source: previewSource, style: previewStyleFn, zIndex: 3 });
    previewLayer.set('id', LYR_PREVIEW);

    const tacticalLayer = new VectorLayer({ source: tacticalSource, style: tacticalFeatureStyle, zIndex: 5 });
    tacticalLayer.set('id', LYR_TACTICAL);

    map.addLayer(trailLayer);
    map.addLayer(markingsLayer);
    map.addLayer(previewLayer);
    map.addLayer(tacticalLayer);

    // Attach canvas overlay for CBRN hatch and minefield perimeter-stamp fills.
    // Stored on the layer object so destroy() can clean it up.
    const areaFillOverlay = new AreaFillOverlay(tacticalLayer, map);
    tacticalLayer.set('_areaFillOverlay', areaFillOverlay);

    return {
        [LYR_FILLS]: markingsLayer,
        [LYR_PREVIEW]: previewLayer,
        [LYR_TACTICAL]: tacticalLayer,
    };
}

function getLayer(map, id) {
    return map.getLayers().getArray().find((l) => l.get('id') === id) ?? null;
}

// ---------------------------------------------------------------------------
// GeoJSON ? OL feature helpers
// ---------------------------------------------------------------------------
function markingsToOlFeatures(markings, layers) {
    const geoFeatures = markingsToGeoJSON(markings, layers);
    return geoFeatures.map((f) => {
        const olFeature = FORMAT.readFeature(f);
        olFeature.setId(f.id);
        olFeature.set('_props', f.properties);
        return olFeature;
    });
}

// Fine-grained delta sync: only rebuild OL features for markings whose
// serialised attrs changed since the last PM transaction.
// ponytail: ceiling — O(n) JSON.stringify scan per transaction; upgrade path is a
// PM appendTransaction plugin that emits dirty markingId sets directly.
//
// ticket 04: `cursorDtg` (epoch ms, or null/undefined for "show latest") is forwarded to
// markingsToGeoJSON so each feature's geometry/`isCurrent` reflect the DTG-cursor-resolved
// snapshot (see resolveTrackAtDtg in schema/mapPmAdapter.js) rather than always the raw
// top-level geometry attr. Tracks not yet "born" at the cursor are excluded from `geoFeatures`
// entirely, so the "incoming" set below is derived from `geoFeatures` (not the raw `markings`
// list) — this makes a track that scrubs out of visibility get its stale OL feature removed,
// same as a genuinely-deleted marking.
//
// ticket 04 (merge-last-known-into-trail): `trailEnabled`/`trailWindowDays` are forwarded to
// markingsToGeoJSON too, so a stale ("last known") pick that's suppressed there (trail off, or
// trail on but beyond `trailWindowDays`) is likewise excluded from `geoFeatures` — same removal
// path as "not yet born" above, i.e. no icon on the map at all, not just a differently-styled one.
function syncMarkingsSourceDelta(map, markings, layers, selectedIds, symbolSettings, prevAttrs, forceAll = false, cursorDtg, trailEnabled, trailWindowDays) {
    const layer = getLayer(map, LYR_FILLS);
    if (!layer) return;
    const source = layer.getSource();

    const geoFeatures = markingsToGeoJSON(markings, layers, cursorDtg, trailEnabled, trailWindowDays);
    const incoming = new Map(geoFeatures.map((f) => [String(f.id), f]));

    // Remove features that no longer exist, or are no longer visible at this DTG cursor.
    source.getFeatures().forEach((f) => {
        const id = String(f.getId());
        if (!incoming.has(id)) {
            source.removeFeature(f);
            prevAttrs.delete(id);
        }
    });

    // Add or update changed features. Include the resolved geometry in the dirty-check key (not
    // just properties) since scrubbing the DTG cursor can change which snapshot's geometry a
    // feature renders without necessarily changing every property (e.g. isCurrent can be
    // unchanged while the picked snapshot's coordinates differ).
    for (const gf of geoFeatures) {
        const markingId = gf.id;
        const attrsKey = `${JSON.stringify(gf.properties)}|${JSON.stringify(gf.geometry)}`;
        const existing = source.getFeatureById(markingId);
        if (!forceAll && existing && prevAttrs.get(markingId) === attrsKey) continue;
        if (existing) source.removeFeature(existing);
        const olF = FORMAT.readFeature(gf);
        olF.setId(gf.id);
        olF.set('_props', gf.properties);
        source.addFeature(olF);
        prevAttrs.set(markingId, attrsKey);
    }

    layer.changed();
}

// ponytail: viewport opts for WebRenderer per API spec — pixelWidth/Height = visible map area,
// viewportBbox = viewable map extent. Re-derived on each call so zoom changes are reflected.
function getViewportOpts(map) {
    const size = map.getSize();
    if (!size) return {};
    const ext = map.getView().calculateExtent(size);
    const [minX, minY] = toLonLat([ext[0], ext[1]]);
    const [maxX, maxY] = toLonLat([ext[2], ext[3]]);
    return {
        pixelWidth:   size[0],
        pixelHeight:  size[1],
        viewportBbox: `${minX},${minY},${maxX},${maxY}`,
    };
}

// ticket 04: `cursorDtg` (epoch ms, or null/undefined for "show latest") resolves each tactical
// marking's snapshot history the same way markingsToGeoJSON does for regular markings, so
// tactical graphics scrub with the DTG cursor too. Tracks not yet visible at the cursor are
// skipped; the resolved snapshot's `isCurrent` flag is stamped onto each rendered sub-feature's
// `_props` so tacticalStyleFn can apply the shared stale-rendering treatment.
//
// ticket 04 (merge-last-known-into-trail): `trailEnabled`/`trailWindowDays` bound how stale a
// "last known" pick may be (via resolveTrackAtDtg's `maxAgeDays`) before it's skipped entirely —
// same as "not yet visible" above — rather than rendered with the badge.
function syncTacticalSource(map, markings, viewportOpts = {}, cursorDtg, trailEnabled = false, trailWindowDays = 7) {
    const layer = getLayer(map, LYR_TACTICAL);
    if (!layer) return;
    const source = layer.getSource();
    source.clear(true);
    const olFeatures = expandTacticalMarkingsToOlFeatures(markings, viewportOpts, cursorDtg, trailEnabled, trailWindowDays);
    olFeatures.forEach((f) => source.addFeature(f));
}

// ticket 05: movement trail — for every `mobility === 'moving'` marking with at least one past
// snapshot inside `[cursorDtg - windowDays, cursorDtg]` (per `getTrailSnapshots`,
// schema/mapPmAdapter.js), add:
//   - one ghost Point feature per past *position* (consecutive-in-time snapshots collapsed onto
//     an identical position render as a single ghost — ticket 04 (dtg-snapshot-rework) —
//     `getTrailSnapshots` already returns one collapsed entry per position, using each run's
//     newest snapshot's data), faded per its interpolated opacity
//     (`_props.kind: 'ghost'`, styled by trailStyleFn -> trailGhostStyle above)
//   - one LineString feature connecting those ghosts, oldest -> newest, through to the track's
//     *current* resolved position (`_props.kind: 'line'`)
// Stationary markings are skipped entirely (deliverable 4 — they don't move, nothing to trail),
// as are tracks whose current/ghost geometry isn't a Point (a trail of symbol positions only
// makes sense for point tracks; lines/polygons have no single "position" to fade+connect).
// Rebuilds the whole source on every call (like syncTacticalSource) rather than delta-syncing —
// the toggle/window-days control is the primary driver of change here, not per-marking edits, so
// the added complexity of a delta sync isn't worth it.
function syncTrailSource(map, markings, layers, cursorDtg, trailEnabled, trailWindowDays) {
    const layer = getLayer(map, LYR_TRAIL);
    if (!layer) return;
    const source = layer.getSource();
    source.clear(true);
    if (!trailEnabled) { layer.changed(); return; }

    const layerMap = new Map(layers.map((l) => [l.layerId, l]));
    for (const m of markings) {
        if (m.mobility !== 'moving') continue; // stationary symbols unaffected by the trail toggle
        if (layerMap.get(m.layerId)?.visible === false) continue;

        const resolved = resolveTrackAtDtg(m, cursorDtg);
        if (!resolved.visible || !resolved.snapshot) continue;
        const currentGeom = resolved.snapshot.geometry;
        if (currentGeom?.type !== 'Point') continue;

        const trail = getTrailSnapshots(m, cursorDtg, trailWindowDays).filter((s) => s.geometry?.type === 'Point');
        if (trail.length === 0) continue; // nothing in the window -> no ghosts, no line

        // ticket 02 (layer-panel-rework): per-layer color removed — all free-drawn/tactical-
        // graphic markings now use the shared fixed default.
        const color = DEFAULT_MARKING_COLOR;

        // ticket 04: each ghost renders sidc/sidcModifiers from its OWN (post-grouping, newest-
        // in-position-group) resolved snapshot, not the track's current props — a ghost is a
        // past state, and after grouping that state is "whatever was true most recently at that
        // position", which may differ from what's true now.
        trail.forEach((snap, i) => {
            const snapProps = snap.props || {};
            const olF = FORMAT.readFeature({ type: 'Feature', geometry: snap.geometry, properties: {} });
            olF.setId(`${m.markingId}-trail-${i}`);
            olF.set('_props', { sidc: snapProps.sidc, sidcModifiers: snapProps.sidcModifiers, color, kind: 'ghost', opacity: snap.opacity });
            source.addFeature(olF);
        });

        // ticket 04: `trail` is already collapsed to one entry per distinct position (see
        // `getTrailSnapshots` -> `groupTrailSnapshotsByPosition`), but the *current* position
        // (resolved separately, above) can still coincide with the newest ghost's position —
        // dedupe consecutive identical coordinates so that doesn't add a redundant zero-length
        // segment to the line, and skip drawing the line altogether if that collapses everything
        // down to a single point (nothing to connect).
        const lineCoords = dedupeConsecutiveCoords([...trail.map((s) => s.geometry.coordinates), currentGeom.coordinates]);
        if (lineCoords.length < 2) continue;
        const lineF = FORMAT.readFeature({ type: 'Feature', geometry: { type: 'LineString', coordinates: lineCoords }, properties: {} });
        lineF.setId(`${m.markingId}-trail-line`);
        lineF.set('_props', { color, kind: 'line' });
        source.addFeature(lineF);
    }
    layer.changed();
}

// ticket 04: drops consecutive duplicate coordinates (exact match) from a trail's line-string
// points — `trail`'s ghosts are already deduped by position (see `getTrailSnapshots` ->
// `groupTrailSnapshotsByPosition`), but the current (separately-resolved) position appended at
// the end can still coincide with the newest ghost, which would otherwise draw a redundant
// zero-length final segment.
function dedupeConsecutiveCoords(coords) {
    return coords.filter((c, i) => i === 0 || JSON.stringify(c) !== JSON.stringify(coords[i - 1]));
}

// ---------------------------------------------------------------------------
// Geometry delta helpers (same logic as before, map-agnostic)
// ---------------------------------------------------------------------------
function applyGeometryDelta(geometry, deltaLng, deltaLat) {
    const shift = ([lng, lat]) => [lng + deltaLng, lat + deltaLat];
    if (geometry.type === 'Point') return { ...geometry, coordinates: shift(geometry.coordinates) };
    if (geometry.type === 'LineString') return { ...geometry, coordinates: geometry.coordinates.map(shift) };
    if (geometry.type === 'Polygon') return { ...geometry, coordinates: geometry.coordinates.map((r) => r.map(shift)) };
    return geometry;
}

function applyVertexMove(geometry, ringIdx, vtxIdx, lonLat) {
    if (geometry.type === 'LineString') {
        const coords = [...geometry.coordinates];
        coords[vtxIdx] = [lonLat[0], lonLat[1]];
        return { ...geometry, coordinates: coords };
    }
    if (geometry.type === 'Polygon') {
        const rings = geometry.coordinates.map((r) => [...r]);
        rings[ringIdx][vtxIdx] = [lonLat[0], lonLat[1]];
        if (vtxIdx === 0) rings[ringIdx][rings[ringIdx].length - 1] = [lonLat[0], lonLat[1]];
        return { ...geometry, coordinates: rings };
    }
    return geometry;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export default function useMapEditor({
    mapRef, editorState, setEditorState,
    currentUser, activeLayerIdRef, remoteChangeRef, symbolSettings,
    pendingTacticalSidcRef,
    // ticket 03: optional "current DTG cursor" value (epoch ms) used to stamp the `dtg` field of
    // each new/appended marking snapshot (see newMarkingSnapshot in mapSchema.js). This is the
    // integration seam for ticket 01's useDtgRange hook. MapEditorModal.jsx wires this up as
    // `currentDtg={dtgRange.valueMs}`. Callers/tests that don't pass it (or pass undefined)
    // fall back to `Date.now()` at each call site below.
    currentDtg,
    // ticket 05: "Show movement trail" toggle + trailing-window-in-days control (MapEditorModal's
    // `MovementTrailControl`, near the DTG timeline). Mirror currentDtg's "just a prop, default
    // falls back gracefully" contract — omitted (both undefined) renders no trail at all, same as
    // before this ticket existed.
    trailEnabled = false,
    trailWindowDays = 7,
}) {
    const [panelLayers, setPanelLayers] = useState([]);
    const [panelViews, setPanelViews] = useState([]);
    const [panelMeta, setPanelMeta] = useState({});
    const [activeMode, setActiveModeState] = useState(null);
    const [mapReady, setMapReady] = useState(false);
    const [activeLayerId, setActiveLayerId] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);
    const [selectedMarkings, setSelectedMarkings] = useState([]);

    // refs that don't need re-renders
    const isSyncingRef = useRef(false);
    const stateRef = useRef(editorState);
    stateRef.current = editorState;
    const symbolSettingsRef = useRef(symbolSettings);
    symbolSettingsRef.current = symbolSettings;
    // ticket 03: mirrors symbolSettingsRef's pattern — kept fresh every render so the
    // mount-effect's closures (registered once) always read the latest DTG cursor value.
    const currentDtgRef = useRef(currentDtg);
    currentDtgRef.current = currentDtg;
    // ticket 05: same "kept fresh every render, read by the mount-effect's closures" pattern for
    // the movement-trail toggle + window-days control.
    const trailEnabledRef = useRef(trailEnabled);
    trailEnabledRef.current = trailEnabled;
    const trailWindowDaysRef = useRef(trailWindowDays);
    trailWindowDaysRef.current = trailWindowDays;
    // ticket 04: tracks the non-DTG portion of the sync effect's deps (editorState, trailEnabled,
    // trailWindowDays) as of its last run, plus the pending throttle timer — see the effect below.
    const tacticalTrailSyncDepsRef = useRef(null);
    const tacticalTrailThrottleRef = useRef(null);
    const selectedIdsRef = useRef(new Set());
    const pendingInflightRef = useRef(null);
    const dragSnapshotRef = useRef(null);
    const dragRafRef = useRef(null);
    const prevMarkingAttrsRef = useRef(new Map()); // markingId → JSON string of attrs; instance-scoped

    // OL interaction refs
    const drawRef = useRef(null);
    const modifyRef = useRef(null);
    const selectRef = useRef(null);
    const snapRef = useRef(null);
    // interactionRef exposed to MapEditorModal for mode switching
    const interactionRef = useRef(null);

    const setEditorStateRef = useRef(setEditorState);
    setEditorStateRef.current = setEditorState;

    const dispatch = useCallback((trOrBuilder) => {
        const setter = setEditorStateRef.current;
        if (!setter) return;
        setter((prev) => {
            if (!prev) return prev;
            const tr = typeof trOrBuilder === 'function' ? trOrBuilder(prev) : trOrBuilder;
            return tr.docChanged ? prev.apply(tr) : prev;
        });
    }, []);

    // ticket 08: derived synchronously from the current doc via useMemo (not a post-commit
    // useEffect) so it's never stale relative to the doc it's read against — e.g. a shape added
    // and then immediately deleted in the same render pass must see the up-to-date count when
    // the delete-confirmation dialog decides whether to warn.
    const layerShapeCounts = useMemo(() => {
        if (!editorState) return {};
        const { markings } = extractMapState(editorState.doc);
        const counts = {};
        for (const m of markings) counts[m.layerId] = (counts[m.layerId] || 0) + 1;
        return counts;
    }, [editorState]);

    // Derive panel state from PM doc
    useEffect(() => {
        if (!editorState) {
            setPanelLayers([]); setPanelViews([]); setPanelMeta({});
            return;
        }
        const { layers, views, meta, markings } = extractMapState(editorState.doc);
        setPanelLayers(layersForPanel(layers));
        setPanelViews(views);
        setPanelMeta(meta || {});

        if (!activeLayerIdRef.current && layers[0]) {
            activeLayerIdRef.current = layers[0].layerId;
            setActiveLayerId(layers[0].layerId);
        }
        const selIds = selectedIdsRef.current;
        // ticket 03: `currentDtg` is a dependency here (not just read once at mount) — scrubbing
        // the DTG cursor while a marking stays selected must re-resolve+re-flatten its panel data
        // live, without requiring the user to reselect it.
        if (selIds.size > 0) setSelectedMarkings(markings.filter((m) => selIds.has(m.markingId)).map((m) => flattenMarkingForPanel(m, currentDtg, trailEnabled, trailWindowDays)));
    }, [editorState, activeLayerIdRef, currentDtg, trailEnabled, trailWindowDays]);

    // Sync PM doc -> OL sources on every state change
    useEffect(() => {
        if (!editorState || !mapReady) return;
        if (pendingInflightRef.current) return;
        const map = mapRef.current;
        if (!map || isSyncingRef.current) return;
        const { markings, layers } = extractMapState(editorState.doc);
        // ticket 04: `currentDtg` (the DTG cursor) is a dependency here — scrubbing it must
        // re-resolve every marking's snapshot and re-sync the OL sources, same as any other
        // editorState change. The delta-diffed regular-marking sync below stays synchronous
        // (cheap even on a busy doc), but the full rebuild-from-scratch tactical/trail sync is
        // throttled when the ONLY thing that changed since the last run is `currentDtg` — i.e. a
        // timeline scrub, which can fire on every pixel of drag — while still firing immediately
        // for any other trigger (a doc edit, or the trail toggle/window-days control).
        syncMarkingsSourceDelta(map, markings, layers, selectedIdsRef.current, symbolSettingsRef.current, prevMarkingAttrsRef.current, false, currentDtg, trailEnabled, trailWindowDays);

        const runTacticalTrailSync = () => {
            syncTacticalSource(map, markings, getViewportOpts(map), currentDtg, trailEnabled, trailWindowDays);
            // ticket 05: the movement trail depends on the same resolved-per-marking snapshot
            // state as the sync above, plus the toggle/window-days control.
            syncTrailSource(map, markings, layers, currentDtg, trailEnabled, trailWindowDays);
        };

        const prevDeps = tacticalTrailSyncDepsRef.current;
        const isDtgScrubOnly = !!prevDeps
            && prevDeps.editorState === editorState
            && prevDeps.trailEnabled === trailEnabled
            && prevDeps.trailWindowDays === trailWindowDays;
        tacticalTrailSyncDepsRef.current = { editorState, trailEnabled, trailWindowDays };

        clearTimeout(tacticalTrailThrottleRef.current);
        if (isDtgScrubOnly) {
            tacticalTrailThrottleRef.current = setTimeout(runTacticalTrailSync, DTG_SCRUB_THROTTLE_MS);
        } else {
            runTacticalTrailSync();
        }

        return () => clearTimeout(tacticalTrailThrottleRef.current);
    }, [editorState, mapReady, mapRef, currentDtg, trailEnabled, trailWindowDays]);

    // Re-render all symbols when render settings change
    useEffect(() => {
        if (!mapReady) return;
        const map = mapRef.current;
        if (!map) return;
        clearSymbolCacheExt();
        clearSymbolStyleCache();
        const { markings, layers } = extractMapState(stateRef.current.doc);
        syncMarkingsSourceDelta(map, markings, layers, selectedIdsRef.current, symbolSettingsRef.current, prevMarkingAttrsRef.current, true, currentDtgRef.current, trailEnabledRef.current, trailWindowDaysRef.current); // forceAll on settings change
        syncTrailSource(map, markings, layers, currentDtgRef.current, trailEnabledRef.current, trailWindowDaysRef.current); // trail ghosts reuse symbolStyleForSidc's cache too
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [symbolSettings]);

    // ticket 02: derived once so the interaction-lifecycle effect below can depend on the
    // primitive uid value instead of the whole `currentUser` object — a new `currentUser`
    // object identity upstream with the same uid must not tear down and rebuild the whole
    // Select/Modify/Snap/Draw interaction set + keydown/moveend listeners.
    const uid = currentUser?.uid || currentUser?.id || null;

    // Map init + OL interaction setup
    useEffect(() => {
        if (!editorState) return;
        let cancelled = false;

        const startInteraction = (map) => {
            if (cancelled) return;

            setupOlLayers(map, symbolSettingsRef.current, selectedIdsRef);

            const { markings, layers } = extractMapState(stateRef.current.doc);
            syncMarkingsSourceDelta(map, markings, layers, new Set(), symbolSettingsRef.current, prevMarkingAttrsRef.current, true, currentDtgRef.current, trailEnabledRef.current, trailWindowDaysRef.current);
            syncTacticalSource(map, markings, getViewportOpts(map), currentDtgRef.current, trailEnabledRef.current, trailWindowDaysRef.current);
            syncTrailSource(map, markings, layers, currentDtgRef.current, trailEnabledRef.current, trailWindowDaysRef.current);

            // Re-render tactical graphics on zoom change.
            // ponytail: change:resolution fires on zoom — tactical graphics are baked to the
            // viewport's pixel bbox at generation time (required by MIL-STD-2525 fixed-pixel-size
            // decorations), so any viewport change — zoom OR pan — leaves them stale otherwise.
            const onResolutionChange = () => {
                const { markings: ms } = extractMapState(stateRef.current.doc);
                syncTacticalSource(map, ms, getViewportOpts(map), currentDtgRef.current, trailEnabledRef.current, trailWindowDaysRef.current);
            };
            map.getView().on('change:resolution', onResolutionChange);

            // Re-render tactical graphics on pan too (see above) — throttled so continuous drag
            // doesn't spam regeneration, mirroring the coord-grid throttle in MapEditorModal.jsx.
            let panResyncTimer = null;
            const onMoveEnd = () => {
                clearTimeout(panResyncTimer);
                panResyncTimer = setTimeout(() => {
                    const { markings: ms } = extractMapState(stateRef.current.doc);
                    syncTacticalSource(map, ms, getViewportOpts(map), currentDtgRef.current, trailEnabledRef.current, trailWindowDaysRef.current);
                }, 200);
            };
            map.on('moveend', onMoveEnd);

            const markingsSource = getLayer(map, LYR_FILLS)?.getSource();

            // Select interaction — pass explicit style so OL's default CircleStyle doesn't replace the SVG icon.
            // ponytail: OL Select overlays selected features on its own layer; without a style prop it falls
            // back to the built-in default (CircleStyle) which turns every icon into a dot.
            const selectStyle = (feature) => {
                const props = feature.get('_props') || {};
                const geomType = feature.getGeometry()?.getType?.();
                const base = markingFeatureStyle(feature, selectedIdsRef.current);
                const baseArr = base ? (Array.isArray(base) ? base : [base]) : [];
                if (geomType === 'Point') {
                    // Red halo ring behind the icon so the symbol stays readable
                    const halo = new Style({ image: new CircleStyle({ radius: 18, stroke: new Stroke({ color: '#ef4444', width: 2.5 }) }) });
                    return [halo, ...baseArr];
                }
                // For lines / polygons: brighten stroke to red
                const highlight = new Style({ stroke: new Stroke({ color: '#ef4444', width: 3 }) });
                return [highlight, ...baseArr];
            };
            const selectIA = new Select({ condition: click, layers: [getLayer(map, LYR_FILLS)], style: selectStyle });
            selectIA.on('select', (e) => {
                const ids = e.selected.map((f) => String(f.getId()));
                selectedIdsRef.current = new Set(ids);
                setSelectedIds(ids);
                const { markings: ms } = extractMapState(stateRef.current.doc);
                setSelectedMarkings(ms.filter((m) => ids.includes(m.markingId)).map((m) => flattenMarkingForPanel(m, currentDtgRef.current, trailEnabledRef.current, trailWindowDaysRef.current)));
                const { layers: ls } = extractMapState(stateRef.current.doc);
                syncMarkingsSourceDelta(map, ms, ls, selectedIdsRef.current, symbolSettingsRef.current, prevMarkingAttrsRef.current, false, currentDtgRef.current, trailEnabledRef.current, trailWindowDaysRef.current);
            });
            map.addInteraction(selectIA);
            selectRef.current = selectIA;

            // Modify (vertex drag) — only on selected features, requiring explicit selection first
            const modifyIA = new Modify({ features: selectIA.getFeatures() });
            modifyIA.on('modifyend', (e) => {
                const state = stateRef.current;
                if (!state) return;
                let tr = state.tr;
                e.features.getArray().forEach((f) => {
                    const id = String(f.getId());
                    const pos = findNodePos(tr.doc, 'map_marking', 'markingId', id);
                    if (pos == null) return;
                    const node = tr.doc.nodeAt(pos);
                    if (!node) return;
                    // read back geometry from the modified OL feature
                    const geojsonF = FORMAT.writeFeatureObject(f, { featureProjection: 'EPSG:3857', dataProjection: 'EPSG:4326' });
                    const newGeom = geojsonF.geometry;
                    const editNow = Date.now();
                    // ticket 01/02: there is no top-level `geometry` attr anymore — a vertex drag
                    // is a geometry-only edit, so `props` is omitted here (not mirrored manually):
                    // appendMarkingSnapshot resolves the correct base snapshot itself (nearest at
                    // or before the DTG cursor being edited) and carries its props forward, so
                    // sidc/label/affiliation are never blanked, and — unlike mirroring from
                    // "the latest snapshot" — the carried-forward props are the right ones even
                    // when dragging a vertex at a historical DTG cursor.
                    tr = tr.setNodeMarkup(pos, null, {
                        ...node.attrs,
                        updatedAt: editNow,
                        updatedBy: uid,
                        snapshots: appendMarkingSnapshot(node.attrs.snapshots, {
                            dtg: currentDtgRef.current ?? editNow,
                            geometry: newGeom,
                        }),
                    });
                });
                if (tr.docChanged) { isSyncingRef.current = true; try { dispatch(tr); } finally { isSyncingRef.current = false; } }
            });
            map.addInteraction(modifyIA);
            modifyRef.current = modifyIA;

            const snapIA = new Snap({ source: markingsSource });
            map.addInteraction(snapIA);
            snapRef.current = snapIA;

            // Keyboard delete
            const handleKey = (e) => {
                if (e.key !== 'Delete' && e.key !== 'Backspace') return;
                // Don't hijack Delete/Backspace while the user is typing in a form field
                // (e.g. SymbolEditor's amplifier/coordinate inputs) — only treat it as
                // "delete selected marking" when focus is on the map/document body itself.
                const target = e.target;
                const tagName = target?.tagName;
                if (tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT' || target?.isContentEditable) return;
                const selected = selectIA.getFeatures().getArray();
                if (!selected.length) return;
                const ids = selected.map((f) => String(f.getId()));
                const state = stateRef.current;
                if (!state) return;
                const positions = [];
                state.doc.descendants((node, pos) => {
                    if (node.type.name === 'map_marking' && ids.includes(node.attrs.markingId)) positions.push(pos);
                });
                positions.sort((a, b) => b - a);
                let tr = state.tr;
                positions.forEach((pos) => { tr = tr.delete(pos, pos + 1); });
                if (tr.docChanged) dispatch(tr);
                selectIA.getFeatures().clear();
                selectedIdsRef.current = new Set();
                setSelectedIds([]);
                setSelectedMarkings([]);
            };
            document.addEventListener('keydown', handleKey);

            // Expose a minimal interactionRef API for MapEditorModal compatibility
            // placedPointCount is updated by the active interaction (generic or custom).
            let _placedPointCount = 0;
            let _customHandle = null; // { getPlacedCount, destroy } for bespoke interactions
            // ponytail (ticket 04): the 'click' listener that notifies opts.onPlacedCountChange
            // for the current _customHandle, so it can be torn down alongside it — replaces the
            // requestAnimationFrame poll MapEditorModal used to run for the whole draw session.
            let _customHandleClickListener = null;
            // ticket (live-preview-audit): stop function for the generic-Draw-interaction live
            // mil-sym preview (see wireGenericDrawLivePreview below) — torn down alongside
            // drawRef.current whenever the mode switches away or the session ends.
            let _stopGenericLivePreview = null;

            interactionRef.current = {
                get placedPointCount() { return _customHandle ? _customHandle.getPlacedCount() : _placedPointCount; },
                setMode: (mode, opts = {}) => {
                    // Tear down any active draw interaction (generic or custom)
                    if (_customHandle) { _customHandle.destroy(); _customHandle = null; }
                    if (_customHandleClickListener) { map.un('click', _customHandleClickListener); _customHandleClickListener = null; }
                    if (_stopGenericLivePreview) { _stopGenericLivePreview(); _stopGenericLivePreview = null; }
                    if (drawRef.current) {
                        // Sentinel objects (for single-click fixed-count path) expose removeFrom
                        // instead of being OL interactions; call the appropriate cleanup.
                        if (typeof drawRef.current.removeFrom === 'function') {
                            drawRef.current.removeFrom(map);
                        } else {
                            map.removeInteraction(drawRef.current);
                        }
                        drawRef.current = null;
                    }
                    _placedPointCount = 0;
                    if (mode === 'select' || !mode) { setActiveModeState(null); return; }

                    // ── Custom bespoke interaction (registered per DrawRule) ──────────
                    const customFactory = opts.drawRule != null ? getDrawInteractionFactory(opts.drawRule) : null;
                    if (customFactory) {
                        // Prefer opts.sidc (passed by handleSelectTacticalSidc) over the ref —
                        // the ref is mirrored from React state and can lag one tick behind
                        // setMode when a symbol is first selected (LINE1 DIRATK reverse / preview).
                        const sidc = opts.sidc ?? pendingTacticalSidcRef?.current;
                    const handle = customFactory(map, {
                        ...opts,
                        sidc,
                        onFinish: (coords, finishOpts = {}) => {
                                const tacticalSidc = pendingTacticalSidcRef?.current;
                                const state2 = stateRef.current;
                                if (!state2 || !coords?.length) return;

                                let layerId = activeLayerIdRef.current;
                                let initTr = null;
                                if (!layerId) {
                                    const ensured = ensureDefaultLayer(state2);
                                    layerId = ensured.layerId;
                                    activeLayerIdRef.current = layerId;
                                    initTr = ensured.tr;
                                }
                                const { layers: ls } = extractMapState(state2.doc);
                                if (ls.find((l) => l.layerId === layerId)?.status === 'approved') return;

                                const now = Date.now();
                                const markingId = crypto.randomUUID ? crypto.randomUUID() : `${now}-${Math.random()}`;

                                // Determine geometry type from coordinate count / shape
                                const geometry = coords.length === 1
                                    ? { type: 'Point', coordinates: coords[0] }
                                    : { type: 'LineString', coordinates: coords };

                                // sidcModifiers may be supplied by the bespoke interaction (e.g. AM width)
                                const extraMods = finishOpts.sidcModifiers || null;

                                if (tacticalSidc) {
                                    const renderCoords = geometry.type === 'Point' ? [geometry.coordinates] : geometry.coordinates;
                                    const renderMods = extraMods || {};
                                    const geojson = renderTacticalGraphic(tacticalSidc, renderCoords, renderMods);
                                    if (geojson?.features) {
                                        const tactSrc = getLayer(map, LYR_TACTICAL)?.getSource();
                                        if (tactSrc) {
                                            geojson.features.forEach((tf, i) => {
                                                const tof = FORMAT.readFeature(tf);
                                                tof.setId(`${tacticalSidc}-${now}-${i}`);
                                                tof.set('_props', { ...(tf.properties || {}), tacticalSidc });
                                                tactSrc.addFeature(tof);
                                            });
                                        }
                                    }
                                    const elemType = geometry.type === 'Point' ? 'tactical-point' : 'tactical-line';
                                    const node = mapSchema.nodes.map_marking.create({
                                        markingId, layerId, mode: elemType, elementType: elemType,
                                        ...creationIdentityAttrs({
                                            uid, now, sidc: tacticalSidc,
                                            sidcModifiers: extraMods ? JSON.stringify(extraMods) : null,
                                            label: '', geometry, dtg: currentDtgRef.current,
                                        }),
                                        graphicType: null,
                                    });
                                    isSyncingRef.current = true;
                                    try { const base = initTr ? state2.apply(initTr) : state2; dispatch(base.tr.insert(base.doc.content.size, node)); }
                                    finally { isSyncingRef.current = false; }
                                    // Revert to select after placement so MapEditorModal clears pending state.
                                    // setTimeout(0) lets the PM state update flush first.
                                    setTimeout(() => { interactionRef.current?.setMode('select'); }, 0);
                                }
                            },
                        });
                        _customHandle = handle;
                        setActiveModeState(mode);

                        // ponytail (ticket 04): event-driven placement counter — notify the caller's
                        // onPlacedCountChange right away (seeds e.g. "0 of N") and again after every
                        // 'click', which is the point-adding event for every bespoke collector in
                        // customInteractions/*. Registered *after* the factory's own 'click' listener
                        // (added synchronously inside customFactory(), above), so per OL's Target event
                        // dispatch (registration order) this always observes the just-updated count —
                        // no requestAnimationFrame polling needed.
                        opts.onPlacedCountChange?.(handle.getPlacedCount());
                        const notifyPlacedCount = () => {
                            if (_customHandle !== handle) return; // superseded by a later setMode call
                            opts.onPlacedCountChange?.(handle.getPlacedCount());
                        };
                        map.on('click', notifyPlacedCount);
                        _customHandleClickListener = notifyPlacedCount;
                        return;
                    }

                    // draw-circle: capture 2 points as a LineString, then on drawend
                    // store a Point geometry + AM modifier = haversine distance in metres.
                    // ponytail: OL has no native geographic-circle draw; 2-pt LineString is
                    // the lightest approach — no custom interaction needed.
                    const isCircle = mode === 'draw-circle';

                    // Fixed-count rules (min === max > 1, not circle): single-click placement.
                    // Generate N spread points around the click and commit immediately.
                    const isFixed = !isCircle && opts.minPoints > 1 && opts.minPoints === opts.maxPoints;
                    if (isFixed) {
                        const singleClickHandler = (e) => {
                            if (e.originalEvent.button !== 0) return;
                            map.un('click', singleClickHandler);

                            const pts3857 = generateSpread(e.coordinate, opts.minPoints, map);
                            const spreadLonLat = pts3857.map((c) => toLonLat(c));
                            const geometry = { type: 'LineString', coordinates: spreadLonLat };

                            const tacticalSidc = pendingTacticalSidcRef?.current;
                            const state2 = stateRef.current;
                            if (!state2) return;

                            let layerId = activeLayerIdRef.current;
                            let initTr2 = null;
                            if (!layerId) {
                                const ensured = ensureDefaultLayer(state2);
                                layerId = ensured.layerId;
                                activeLayerIdRef.current = layerId;
                                initTr2 = ensured.tr;
                            }
                            const { layers: ls2 } = extractMapState(state2.doc);
                            if (ls2.find((l) => l.layerId === layerId)?.status === 'approved') return;

                            const now = Date.now();
                            const markingId = crypto.randomUUID ? crypto.randomUUID() : `${now}-${Math.random()}`;

                            if (tacticalSidc) {
                                const geojson = renderTacticalGraphic(tacticalSidc, geometry.coordinates, {});
                                if (geojson?.features) {
                                    const tactSrc = getLayer(map, LYR_TACTICAL)?.getSource();
                                    if (tactSrc) {
                                        geojson.features.forEach((tf, i) => {
                                            const tof = FORMAT.readFeature(tf);
                                            tof.setId(`${tacticalSidc}-${now}-${i}`);
                                            tof.set('_props', { ...(tf.properties || {}), tacticalSidc });
                                            tactSrc.addFeature(tof);
                                        });
                                    }
                                }
                                const node = mapSchema.nodes.map_marking.create({
                                    markingId, layerId, mode: 'tactical-line', elementType: 'tactical-line',
                                    ...creationIdentityAttrs({ uid, now, sidc: tacticalSidc, sidcModifiers: null, label: '', geometry, dtg: currentDtgRef.current }),
                                    graphicType: null,
                                });
                                isSyncingRef.current = true;
                                try { const base = initTr2 ? state2.apply(initTr2) : state2; dispatch(base.tr.insert(base.doc.content.size, node)); }
                                finally { isSyncingRef.current = false; }
                            } else {
                                const node = mapSchema.nodes.map_marking.create({
                                    markingId, layerId, mode: 'free-line', elementType: 'free-line',
                                    ...creationIdentityAttrs({ uid, now, sidc: null, sidcModifiers: null, label: '', geometry, dtg: currentDtgRef.current }),
                                    graphicType: null,
                                });
                                isSyncingRef.current = true;
                                try { const base = initTr2 ? state2.apply(initTr2) : state2; dispatch(base.tr.insert(base.doc.content.size, node)); }
                                finally { isSyncingRef.current = false; }
                            }

                            // Revert to select after placement.
                            setTimeout(() => { interactionRef.current?.setMode('select'); }, 0);
                        };

                        // Use a sentinel drawRef so the existing cleanup in setMode
                        // (which calls map.removeInteraction) degrades gracefully on
                        // a non-OL-interaction object.
                        drawRef.current = { removeFrom: (m) => m.un('click', singleClickHandler) };
                        map.on('click', singleClickHandler);
                        setActiveModeState(mode);
                        return;
                    }

                    const geomType =
                        mode === 'draw-point'  ? 'Point'      :
                        isCircle               ? 'LineString' :
                        mode === 'draw-line'   ? 'LineString' :
                        mode === 'draw-polygon'? 'Polygon'    :
                        mode === 'draw-freehand'? 'LineString' : null;
                    if (!geomType) return;

                    // ponytail: draw-point uses a throwaway source so the raw click-dot
                    // never lands in markingsSource; tactical render goes straight to LYR_TACTICAL.
                    const drawSource = mode === 'draw-point' ? new VectorSource() : markingsSource;

                    const drawOpts = {
                        source: drawSource,
                        type: geomType,
                        freehand: mode === 'draw-freehand',
                        condition: (e) => e.originalEvent.button === 0,
                        stopClick: true,
                        ...(isCircle ? { style: CIRCLE_SKETCH_STYLES } : {}),
                    };
                    // Cap at maxPoints; OL auto-finishes when reached.
                    // draw-circle always caps at 2. Number.MAX_VALUE means unlimited — don't pass.
                    const effectiveMax = isCircle ? 2 : opts.maxPoints;
                    if (effectiveMax && effectiveMax < Number.MAX_VALUE) drawOpts.maxPoints = effectiveMax;

                    const drawIA = new Draw(drawOpts);

                    // Live mil-sym preview for unlimited-point LINE/AREA rules drawn via this
                    // generic OL Draw interaction (e.g. LINE2/7/8/13/21, AREA1/3/4/9/20/23) —
                    // LINE1 uses line1DirectionOfAttackTools (DIRATK reverse + live preview).
                    // without this, users only saw OL's plain default sketch outline while
                    // placing points, with the real tactical symbol appearing only after
                    // drawend. Hooks the Draw interaction's own in-progress sketch geometry
                    // (via 'drawstart' + the sketch feature's 'change' event) straight into
                    // renderTacticalGraphic/the shared preview layer used by livePreview.js's
                    // click-collector-driven preview, so both paths render through the same
                    // renderer and land on the same layer.
                    const genericPreviewSidc = pendingTacticalSidcRef?.current;
                    if (genericPreviewSidc && (mode === 'draw-line' || mode === 'draw-polygon') && !isCircle) {
                        const previewLayer = ensurePreviewLayer(map, genericPreviewSidc);
                        let sketchGeomKey = null;

                        const renderSketchPreview = (sketchFeature) => {
                            const source = previewLayer.getSource();
                            source.clear(true);
                            const geom = sketchFeature.getGeometry();
                            if (!geom) return;
                            const g = FORMAT.writeGeometryObject(geom, { featureProjection: 'EPSG:3857', dataProjection: 'EPSG:4326' });
                            const coords = g.type === 'Polygon' ? g.coordinates[0] : g.coordinates;
                            if (!coords || coords.length < 2) return;
                            const geojson = renderTacticalGraphic(genericPreviewSidc, coords, {});
                            if (!geojson?.features) return;
                            geojson.features.forEach((tf, i) => {
                                try {
                                    const tof = FORMAT.readFeature(tf);
                                    tof.setId(`sketch-preview-${i}`);
                                    source.addFeature(tof);
                                } catch (_) {}
                            });
                        };

                        const onDrawStart = (e) => {
                            sketchGeomKey = e.feature.getGeometry().on('change', () => renderSketchPreview(e.feature));
                        };
                        drawIA.on('drawstart', onDrawStart);

                        _stopGenericLivePreview = () => {
                            if (sketchGeomKey) { unByKey(sketchGeomKey); sketchGeomKey = null; }
                            drawIA.un('drawstart', onDrawStart);
                            clearPreviewLayer(map);
                        };
                    }

                    drawIA.on('drawend', (e) => {
                        // Clear the throwaway source immediately so no raw dot persists.
                        if (mode === 'draw-point') drawSource.clear();
                        if (_stopGenericLivePreview) { _stopGenericLivePreview(); _stopGenericLivePreview = null; }

                        const olF = e.feature;
                        const geojsonF = FORMAT.writeFeatureObject(olF, { featureProjection: 'EPSG:3857', dataProjection: 'EPSG:4326' });
                        let geometry = geojsonF.geometry;

                        // draw-circle: convert 2-pt LineString → Point + AM radius (metres).
                        let extraModifiers = null;
                        if (isCircle && geometry.type === 'LineString' && geometry.coordinates.length === 2) {
                            const [center, edge] = geometry.coordinates; // [lng, lat]
                            const radiusM = Math.round(getDistance(center, edge));
                            geometry = { type: 'Point', coordinates: center };
                            extraModifiers = { [Modifiers.AM_DISTANCE]: String(radiusM) };
                        }

                        // Enforce minimum point count for non-circle modes.
                        if (!isCircle && opts.minPoints && opts.minPoints > 1) {
                            const coords = geometry.type === 'Polygon' ? geometry.coordinates[0] : geometry.coordinates;
                            const placed = geometry.type === 'Polygon' ? coords.length - 1 : coords.length;
                            if (placed < opts.minPoints) { e.feature.setGeometry(null); return; }
                        }

                        const tacticalSidc = pendingTacticalSidcRef?.current;
                        const state2 = stateRef.current;
                        if (!state2) return;

                        let layerId = activeLayerIdRef.current;
                        let initTr = null;
                        if (!layerId) {
                            const ensured = ensureDefaultLayer(state2);
                            layerId = ensured.layerId;
                            activeLayerIdRef.current = layerId;
                            initTr = ensured.tr;
                        }
                        const { layers: ls } = extractMapState(state2.doc);
                        if (ls.find((l) => l.layerId === layerId)?.status === 'approved') return;

                        const now = Date.now();
                        const markingId = crypto.randomUUID ? crypto.randomUUID() : `${now}-${Math.random()}`;

                        if (tacticalSidc && geometry.type !== 'Point') {
                            const coords = geometry.type === 'Polygon' ? geometry.coordinates[0] : geometry.coordinates;
                            const geojson = renderTacticalGraphic(tacticalSidc, coords, extraModifiers || {});
                            if (geojson?.features) {
                                const tactSrc = getLayer(map, LYR_TACTICAL)?.getSource();
                                if (tactSrc) {
                                    geojson.features.forEach((tf, i) => {
                                        const tof = FORMAT.readFeature(tf);
                                        tof.setId(`${tacticalSidc}-${now}-${i}`);
                                        tof.set('_props', { ...(tf.properties || {}), tacticalSidc });
                                        tactSrc.addFeature(tof);
                                    });
                                }
                            }
                            const elemType = geometry.type === 'Polygon' ? 'tactical-area' : 'tactical-line';
                            const node = mapSchema.nodes.map_marking.create({
                                markingId, layerId, mode: elemType, elementType: elemType,
                                ...creationIdentityAttrs({
                                    uid, now, sidc: tacticalSidc,
                                    sidcModifiers: extraModifiers ? JSON.stringify(extraModifiers) : null,
                                    label: '', geometry, dtg: currentDtgRef.current,
                                }),
                                graphicType: null,
                            });
                            isSyncingRef.current = true;
                            try { const base = initTr ? state2.apply(initTr) : state2; dispatch(base.tr.insert(base.doc.content.size, node)); }
                            finally { isSyncingRef.current = false; }
                            return;
                        }

                        const elemType = tacticalSidc ? 'tactical-point' : elementTypeFromGeom(geometry.type);
                        const sidcModifiers = extraModifiers ? JSON.stringify(extraModifiers) : null;
                        const node = mapSchema.nodes.map_marking.create({
                            markingId, layerId, mode: elemType, elementType: elemType,
                            ...creationIdentityAttrs({ uid, now, sidc: tacticalSidc || null, sidcModifiers, label: '', geometry, dtg: currentDtgRef.current }),
                            graphicType: null,
                        });
                        isSyncingRef.current = true;
                        try { const base = initTr ? state2.apply(initTr) : state2; dispatch(base.tr.insert(base.doc.content.size, node)); }
                        finally { isSyncingRef.current = false; }
                    });

                    map.addInteraction(drawIA);
                    drawRef.current = drawIA;
                    setActiveModeState(mode);
                },
                setSelectedIds: (ids) => {
                    selectedIdsRef.current = new Set(ids.map(String));
                    selectIA.getFeatures().clear();
                    setSelectedIds(ids);
                    if (!ids.length) setSelectedMarkings([]);
                },
                get selectedIds() { return [...selectedIdsRef.current]; },
                // Allow MapEditorModal to emit synthetic 'finish' events for drag-drop / click-place
                _emit: (event, payload) => {
                    if (event !== 'finish') return;
                    const { geometry, sidc, geomType } = payload;
                    const state2 = stateRef.current;
                    if (!state2) return;
                    let layerId = activeLayerIdRef.current;
                    let initTr = null;
                    if (!layerId) {
                        const ensured = ensureDefaultLayer(state2);
                        layerId = ensured.layerId;
                        activeLayerIdRef.current = layerId;
                        initTr = ensured.tr;
                    }
                    const now = Date.now();
                    const markingId = crypto.randomUUID ? crypto.randomUUID() : `${now}-${Math.random()}`;
                    const elemType = sidc ? 'unit-icon' : elementTypeFromGeom(geomType || geometry.type);
                    const node = mapSchema.nodes.map_marking.create({
                        markingId, layerId, mode: elemType, elementType: elemType,
                        ...creationIdentityAttrs({ uid, now, sidc: sidc || null, sidcModifiers: null, label: '', geometry, dtg: currentDtgRef.current }),
                        graphicType: null,
                    });
                    isSyncingRef.current = true;
                    try {
                        const base = initTr ? state2.apply(initTr) : state2;
                        dispatch(base.tr.insert(base.doc.content.size, node));
                    } finally { isSyncingRef.current = false; }
                },
                destroy: () => {
                    document.removeEventListener('keydown', handleKey);
                    map.getView().un('change:resolution', onResolutionChange);
                    map.un('moveend', onMoveEnd);
                    clearTimeout(panResyncTimer);
                    if (_customHandle) { _customHandle.destroy(); _customHandle = null; }
                    if (_customHandleClickListener) { map.un('click', _customHandleClickListener); _customHandleClickListener = null; }
                    if (_stopGenericLivePreview) { _stopGenericLivePreview(); _stopGenericLivePreview = null; }
                    if (drawRef.current) { map.removeInteraction(drawRef.current); drawRef.current = null; }
                    if (modifyRef.current) { map.removeInteraction(modifyRef.current); modifyRef.current = null; }
                    if (selectRef.current) { map.removeInteraction(selectRef.current); selectRef.current = null; }
                    if (snapRef.current) { map.removeInteraction(snapRef.current); snapRef.current = null; }
                    // Destroy canvas overlay
                    getLayer(map, LYR_TACTICAL)?.get('_areaFillOverlay')?.destroy();
                },
            };

            setMapReady(true);
        };

        const tryInit = () => {
            if (cancelled) return;
            const map = mapRef.current;
            if (!map) { setTimeout(tryInit, 50); return; }
            startInteraction(map);
        };
        tryInit();

        return () => {
            cancelled = true;
            interactionRef.current?.destroy();
            interactionRef.current = null;
            setMapReady(false);
        };
    }, [mapRef, uid, dispatch, activeLayerIdRef]);

    // -- Mode control ---------------------------------------------------------
    const setMode = useCallback((modeName, opts = {}) => {
        const ia = interactionRef.current;
        if (!ia) return;
        if (!modeName || modeName === 'select') {
            ia.setMode('select');
            setActiveModeState(null);
            return;
        }
        const state = stateRef.current;
        if (state && modeName !== 'select') {
            const { layers } = extractMapState(state.doc);
            const activeLayer = layers.find((l) => l.layerId === activeLayerIdRef.current);
            if (activeLayer?.status === 'approved') return;
        }
        ia.setMode(modeName, opts);
    }, [activeLayerIdRef]);

    // -- Marking property updates (from right panel) --------------------------
    const updateMarkingProps = useCallback((markingId, patch) => {
        const state = stateRef.current;
        if (!state) return;
        const pos = findNodePos(state.doc, 'map_marking', 'markingId', markingId);
        if (pos == null) return;
        const node = state.doc.nodeAt(pos);
        if (!node) return;
        const uid = currentUser?.uid || currentUser?.id || null;
        const editNow = Date.now();
        const snapDtg = currentDtgRef.current ?? editNow;

        // ticket 01: `sidc`/`sidcModifiers`/`label`/`geometry` are no longer top-level marking
        // attrs — route those patch keys (plus a derived `affiliation` whenever `sidc` changes)
        // into the snapshot's `props`/`geometry` instead of onto the node directly. Any other
        // patch keys (e.g. `mobility`) are genuine top-level marking attrs, applied as before.
        const { geometry: patchedGeometry, sidc, sidcModifiers, label, ...attrPatch } = patch;
        const snapshotProps = {};
        if (sidc !== undefined) {
            snapshotProps.sidc = sidc;
            snapshotProps.affiliation = sidc ? getAffiliationForSidc(sidc) : null;
        }
        if (sidcModifiers !== undefined) snapshotProps.sidcModifiers = sidcModifiers;
        if (label !== undefined) snapshotProps.label = label;

        // ticket 02: geometry/props for a snapshot this edit doesn't touch are no longer mirrored
        // here from "the latest snapshot overall" — appendMarkingSnapshot resolves the correct
        // base (the snapshot at-or-before `snapDtg`, i.e. relative to the DTG cursor the user is
        // actually scrubbed to) itself and mirrors forward whatever this patch omits, so a
        // prop-only edit at a historical DTG forks from that DTG's own predecessor rather than
        // from a snapshot that may be *later* than the one being edited.
        dispatch(state.tr.setNodeMarkup(pos, null, {
            ...node.attrs,
            ...attrPatch,
            updatedAt: editNow,
            updatedBy: uid,
            snapshots: appendMarkingSnapshot(node.attrs.snapshots, {
                dtg: snapDtg,
                geometry: patchedGeometry,
                props: snapshotProps,
            }),
        }));
        setSelectedMarkings((prev) => prev.map((m) => m.markingId === markingId ? { ...m, ...patch } : m));
    }, [dispatch, currentUser]);

    // -- Active layer ---------------------------------------------------------
    const setActiveLayer = useCallback((layerId) => {
        activeLayerIdRef.current = layerId;
        setActiveLayerId(layerId);
    }, [activeLayerIdRef]);

    // -- Layer CRUD -----------------------------------------------------------
    // layer-panel-audit ticket 01: every handler below builds its transaction inside
    // dispatch's functional-updater form (`dispatch(prev => ...)`) rather than from
    // `stateRef.current` (a ref only refreshed on render). Building from `stateRef.current`
    // meant a second rapid call — before the first click's state update had propagated back
    // into the ref — produced a transaction whose base doc no longer matched the live state,
    // so ProseMirror's `applyInner` threw "Applying a mismatched transaction" and the second
    // call's change was silently dropped. Building from the callback's `prev` guarantees the
    // base doc is always the one that's about to be applied, so N rapid calls always compound
    // correctly.
    const handleUpdateLayer = useCallback((layerId, updates) => {
        dispatch((prev) => {
            const pos = findNodePos(prev.doc, 'map_layer', 'layerId', layerId);
            if (pos == null) return prev.tr;
            const node = prev.doc.nodeAt(pos);
            return prev.tr.setNodeMarkup(pos, null, { ...node.attrs, ...updates });
        });
    }, [dispatch]);

    const handleDeleteLayer = useCallback((layerId) => {
        dispatch((prev) => {
            const positions = [];
            prev.doc.descendants((node, pos) => {
                if ((node.type.name === 'map_layer' && node.attrs.layerId === layerId) ||
                    (node.type.name === 'map_marking' && node.attrs.layerId === layerId)) positions.push(pos);
            });
            positions.sort((a, b) => b - a);
            let tr = prev.tr;
            positions.forEach((pos) => { tr = tr.delete(pos, pos + 1); });
            return tr;
        });
    }, [dispatch]);

    const handleReorderLayer = useCallback((sourceIndex, targetIndex) => {
        dispatch((prev) => {
            const { layers } = extractMapState(prev.doc);
            const arr = [...layers];
            const [moved] = arr.splice(sourceIndex, 1);
            arr.splice(targetIndex, 0, moved);
            let tr = prev.tr;
            arr.forEach((layer, i) => {
                const pos = findNodePos(tr.doc, 'map_layer', 'layerId', layer.layerId);
                if (pos != null) { const n = tr.doc.nodeAt(pos); tr = tr.setNodeMarkup(pos, null, { ...n.attrs, order: i }); }
            });
            return tr;
        });
    }, [dispatch]);

    const handleAddLayer = useCallback(() => {
        const layerId = newLayerId();
        // ticket 01 (layer-panel-rework): stamp the creating user as the layer's owner — same
        // uid derivation used for marking author/updatedBy elsewhere in this file. `null` (no
        // signed-in user) falls back to the schema default, which is treated as "unowned"/
        // owned-by-everyone.
        const ownerId = currentUser?.uid || currentUser?.id || null;
        dispatch((prev) => {
            const { layers } = extractMapState(prev.doc);
            // layer-panel-audit ticket 05: derive the default label from the highest existing
            // numeric "Layer N" suffix + 1, not `layers.length + 1` — the latter collides with
            // a still-existing layer's label after any delete-then-add sequence that isn't
            // strictly append-only (e.g. add "Layer 1"/"Layer 2", delete "Layer 1", add again
            // would otherwise also produce "Layer 2").
            let maxSuffix = 0;
            for (const l of layers) {
                const m = /^Layer (\d+)$/.exec(l.label || '');
                if (m) maxSuffix = Math.max(maxSuffix, parseInt(m[1], 10));
            }
            const node = mapSchema.nodes.map_layer.create({
                layerId, label: `Layer ${maxSuffix + 1}`, visible: true, order: layers.length, ownerId,
            });
            return prev.tr.insert(prev.doc.content.size, node);
        });
        activeLayerIdRef.current = layerId;
        setActiveLayerId(layerId);
    }, [dispatch, activeLayerIdRef, currentUser]);

    // -- Undo / redo ----------------------------------------------------------
    const handleUndo = useCallback(() => { const s = stateRef.current; if (s) undo(s, dispatch); }, [dispatch]);
    const handleRedo = useCallback(() => { const s = stateRef.current; if (s) redo(s, dispatch); }, [dispatch]);

    // -- Delete selected ------------------------------------------------------
    const handleDeleteSelected = useCallback(() => {
        const ia = interactionRef.current;
        if (!ia) return;
        const ids = ia.selectedIds;
        if (!ids.length) return;
        const state = stateRef.current;
        if (!state) return;
        const positions = [];
        state.doc.descendants((node, pos) => {
            if (node.type.name === 'map_marking' && ids.includes(node.attrs.markingId)) positions.push(pos);
        });
        positions.sort((a, b) => b - a);
        let tr = state.tr;
        positions.forEach((pos) => { tr = tr.delete(pos, pos + 1); });
        if (tr.docChanged) dispatch(tr);
        ia.setSelectedIds([]);
    }, [dispatch]);

    // -- Meta -------------------------------------------------------------------
    // shared-map-data ticket 03: `map_meta` only holds the shared DTG range now (`dtgStartMs`/
    // `dtgEndMs` — see mapSchema.js) — `title`/`basemapStyleId`/`activeGridId`/`overlayType`/
    // `currentDtgMs` moved to per-node mapNode attrs, written via MapNodeView's
    // `_onWindowSettingsChange` (see MapEditorModal.jsx's `onWindowSettingsChange` prop) instead
    // of this function. Kept as `updateMeta` (not renamed/split) since `map_meta` itself hasn't
    // gone away, just shrunk to one genuinely doc-level field.
    const updateMeta = useCallback((updates) => {
        dispatch((prev) => {
            let metaPos = null;
            prev.doc.descendants((node, pos) => { if (node.type.name === 'map_meta') metaPos = pos; });
            if (metaPos == null) return prev.tr;
            const node = prev.doc.nodeAt(metaPos);
            return prev.tr.setNodeMarkup(metaPos, null, { ...node.attrs, ...updates });
        });
    }, [dispatch]);

    // -- Views ----------------------------------------------------------------
    const createViewFromBbox = useCallback((name, bbox) => {
        if (!bbox) return null;
        const viewId = newViewId();
        dispatch((prev) => {
            const viewNode = mapSchema.nodes.map_view.create({ viewId, name: name || 'View', bbox });
            return prev.tr.insert(prev.doc.content.size, viewNode);
        });
        return viewId;
    }, [dispatch]);

    const updateViewBbox = useCallback((viewId, bbox) => {
        const state = stateRef.current;
        if (!state) return;
        const pos = findNodePos(state.doc, 'map_view', 'viewId', viewId);
        if (pos == null) return;
        const node = state.doc.nodeAt(pos);
        dispatch(state.tr.setNodeMarkup(pos, null, { ...node.attrs, bbox }));
    }, [dispatch]);

    const renameView = useCallback((viewId, name) => {
        const state = stateRef.current;
        if (!state) return;
        const pos = findNodePos(state.doc, 'map_view', 'viewId', viewId);
        if (pos == null) return;
        const node = state.doc.nodeAt(pos);
        dispatch(state.tr.setNodeMarkup(pos, null, { ...node.attrs, name }));
    }, [dispatch]);

    const deleteView = useCallback((viewId) => {
        const state = stateRef.current;
        if (!state) return;
        const pos = findNodePos(state.doc, 'map_view', 'viewId', viewId);
        if (pos == null) return;
        dispatch(state.tr.delete(pos, pos + 1));
    }, [dispatch]);

    return {
        interactionRef, stateRef,
        layers: panelLayers, views: panelViews, meta: panelMeta,
        activeMode, activeLayerId, layerShapeCounts, selectedIds, selectedMarkings,
        setMode, setActiveLayer, mapReady,
        canUndo: editorState ? undoDepth(editorState) > 0 : false,
        canRedo: editorState ? redoDepth(editorState) > 0 : false,
        handleUpdateLayer, handleDeleteLayer, handleReorderLayer, handleAddLayer,
        handleUndo, handleRedo, handleDeleteSelected,
        createViewFromBbox, updateViewBbox, renameView, deleteView,
        updateMeta, updateMarkingProps, dispatch,
    };
}
